import { Link } from "react-router-dom";
import { CalendarDays, CalendarCheck, Plus, Zap, ChevronRight, GraduationCap } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useFetch } from "../lib/api";
import { Loading } from "../components/bits";

function Todo({ to, icon, text, testid }) {
  return (
    <Link to={to} data-testid={testid} className="card tap flex items-center gap-3 px-4 py-4">
      <span className="text-xl">{icon}</span>
      <span className="flex-1 font-semibold text-slate-800">{text}</span>
      <ChevronRight className="h-5 w-5 text-slate-300" />
    </Link>
  );
}

function ManagerHome({ d }) {
  const items = [];
  if (d.uncovered.length) items.push(<Todo key="u" to="/planning" icon="🔴" text={`${d.uncovered.length} jour(s) sans infirmière`} testid="todo-uncovered" />);
  if (d.pending.length) items.push(<Todo key="p" to="/disponibilites" icon="🟠" text={`${d.pending.length} personne(s) n'ont pas encore transmis leurs disponibilités`} testid="todo-pending" />);
  if (d.to_assign) items.push(<Todo key="m" to="/missions" icon="⚡" text={`${d.to_assign} mission(s) en attente d'attribution`} testid="todo-missions" />);
  if (d.replacements) items.push(<Todo key="r" to="/missions" icon="🔁" text={`${d.replacements} demande(s) de remplacement`} testid="todo-replacements" />);
  if (d.todo_month) items.push(<Todo key="a" to={`/disponibilites?m=${d.todo_month}`} icon="📅" text={`Encoder mes disponibilités de ${d.todo_label.split(" ")[0]}`} testid="todo-my-availability" />);
  return (
    <>
      <h2 className="eyebrow mb-2">À traiter</h2>
      <div className="mb-8 space-y-2 fade-up">
        {items.length ? items : <div className="card px-4 py-6 text-center font-semibold text-emerald-700" data-testid="todo-none">✓ Tout est en ordre</div>}
      </div>
      <div className="grid gap-3 fade-up">
        <Link to="/planning" className="btn-primary !min-h-[56px]" data-testid="home-planning-btn"><CalendarDays className="h-5 w-5" /> Planning</Link>
        <div className="grid grid-cols-2 gap-3">
          <Link to="/missions/nouvelle" className="btn-soft !min-h-[56px]" data-testid="home-new-mission-btn"><Plus className="h-5 w-5" /> Mission</Link>
          <Link to="/disponibilites" className="btn-ghost !min-h-[56px]" data-testid="home-availability-btn"><CalendarCheck className="h-5 w-5" /> Disponibilités</Link>
        </div>
      </div>
    </>
  );
}

function NurseHome({ d }) {
  return (
    <>
      <h2 className="eyebrow mb-2">Prochaine prestation</h2>
      <div className="card mb-7 p-5 fade-up" data-testid="nurse-next-shift">
        {d.next ? (
          <>
            <div className="font-display text-xl font-extrabold">{d.next.label.charAt(0).toUpperCase() + d.next.label.slice(1)}</div>
            <div className="mt-1 text-lg text-slate-700">{d.next.title} · <span className="text-teal-600">{d.next.team}</span></div>
            {d.next.start && <div className="font-mono text-teal-700">{d.next.start} → {d.next.end}</div>}
          </>
        ) : <p className="text-slate-500">Rien de prévu pour le moment.</p>}
      </div>
      {(d.todo_month || d.missions_available > 0) && (
        <>
          <h2 className="eyebrow mb-2">À faire</h2>
          <div className="mb-8 space-y-2 fade-up">
            {d.todo_month && <Todo to={`/disponibilites?m=${d.todo_month}`} icon="🟠" text={`Encoder mes disponibilités de ${d.todo_label.split(" ")[0]}`} testid="todo-availability" />}
            {d.missions_available > 0 && <Todo to="/missions" icon="⚡" text={`${d.missions_available} nouvelle(s) mission(s)`} testid="todo-missions-available" />}
          </div>
        </>
      )}
      <div className="grid grid-cols-2 gap-3 fade-up">
        <Link to="/disponibilites" className="btn-primary !min-h-[56px]" data-testid="nurse-availability-btn"><CalendarCheck className="h-5 w-5" /> Mes dispos</Link>
        <Link to="/missions" className="btn-soft !min-h-[56px]" data-testid="nurse-see-missions-btn"><Zap className="h-5 w-5" /> Missions</Link>
      </div>
    </>
  );
}

function HelpLink() {
  const { data } = useFetch("/training");
  if (!data || !data.total) return null;
  const finished = data.done >= data.total;
  return (
    <Link to="/aide" className="mt-8 flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm transition-colors hover:bg-white fade-up" data-testid="home-help-link">
      <GraduationCap className="h-5 w-5 text-teal-700" />
      <span className="flex-1 font-semibold text-slate-700">Centre d'aide et de formation</span>
      <span className="text-xs font-bold text-slate-500" data-testid="home-help-progress">{finished ? "✓ Terminé" : `${data.done}/${data.total}`}</span>
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </Link>
  );
}

export default function Home() {
  const { sp, user } = useApp();
  const { data } = useFetch(sp("/dashboard"), 30000);
  return (
    <div data-testid="home-page">
      <h1 className="mb-7 text-3xl font-extrabold fade-up">Bonjour {user.first_name}</h1>
      {!data ? <Loading /> : data.manager ? <ManagerHome d={data} /> : <NurseHome d={data} />}
      <HelpLink />
    </div>
  );
}
